import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/common/Button';
import { TILL_NUMBER, TILL_NAME } from '../utils/constants';
import useApplicationStore from '../stores/applicationStore';
import useAuthStore from '../stores/authStore';

const PaymentInstructions = () => {
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const { selectedServiceFee } = useApplicationStore();
  const { profile, isAuthenticated } = useAuthStore();

  useEffect(() => {
    // Redirect if no service fee selected or profile not complete
    if (!selectedServiceFee || !isAuthenticated || !profile?.profileCompleted) {
      navigate('/service-fee-selection');
    }
  }, [selectedServiceFee, isAuthenticated, profile, navigate]);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleContinue = () => {
    navigate('/payment-verification');
  };

  const handleBack = () => {
    navigate('/service-fee-selection');
  };

  // Show loading if no service fee data
  if (!selectedServiceFee) {
    return (
      <div className="page-container">
        <div className="form-container">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="form-container">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Payment Instructions
          </h1>
          <p className="text-gray-600">
            Pay the service fee to proceed with your loan application
          </p>
        </div>

        <div className="bg-primary-50 rounded-xl p-6 mb-6">
          <h2 className="text-lg font-semibold text-primary-900 mb-4">
            Loan Summary
          </h2>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-primary-700">Loan Amount:</span>
              <span className="font-semibold text-primary-900">KES {selectedServiceFee.loanAmount.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-primary-700">Service Fee:</span>
              <span className="font-semibold text-primary-900">KES {selectedServiceFee.fee}</span>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-xl p-6 mb-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
            <svg className="w-5 h-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            M-Pesa Payment Steps
          </h3>
          
          <div className="space-y-4">
            <div className="flex items-start">
              <div className="w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 mt-0.5">
                1
              </div>
              <div>
                <p className="font-medium text-gray-900">Open M-Pesa on your phone</p>
                <p className="text-sm text-gray-600">Go to M-Pesa menu</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 mt-0.5">
                2
              </div>
              <div>
                <p className="font-medium text-gray-900">Select "Lipa na M-Pesa"</p>
                <p className="text-sm text-gray-600">Choose "Buy Goods and Services"</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 mt-0.5">
                3
              </div>
              <div>
                <p className="font-medium text-gray-900">Enter Till Number</p>
                <div className="flex items-center mt-1">
                  <span className="bg-white border rounded px-3 py-1 font-mono text-lg font-bold mr-2">
                    {TILL_NUMBER}
                  </span>
                  <button
                    onClick={() => copyToClipboard(TILL_NUMBER)}
                    className="text-primary-600 hover:text-primary-700 text-sm"
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 mt-0.5">
                4
              </div>
              <div>
                <p className="font-medium text-gray-900">Enter Amount</p>
                <p className="text-sm text-gray-600">KES {selectedServiceFee.fee}</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 mt-0.5">
                5
              </div>
              <div>
                <p className="font-medium text-gray-900">Enter PIN and confirm</p>
                <p className="text-sm text-gray-600">You'll receive an M-Pesa confirmation message</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-green-50 rounded-xl p-4 mb-6">
          <div className="flex items-start">
            <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
              <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-green-800 text-sm font-medium mb-1">
                After payment, you'll need to submit your M-Pesa confirmation message
              </p>
              <p className="text-green-700 text-sm">
                Keep your M-Pesa message ready for the next step
              </p>
            </div>
          </div>
        </div>

        <div className="flex gap-4">
          <Button
            type="button"
            variant="outline"
            size="lg"
            className="flex-1"
            onClick={handleBack}
          >
            Back
          </Button>
          <Button
            type="button"
            size="lg"
            className="flex-1"
            onClick={handleContinue}
          >
            I've Made Payment
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentInstructions;